/**
 * DEVAMS-2144: AppGroupBridgePlugin — Capacitor 7 TypeScript interface.
 *
 * On iOS the native Swift plugin (AppGroupBridgePlugin.swift in the main App
 * target) reads the file written by the ShareExtension from the App Group
 * container and returns it as a base64-encoded string.
 *
 * A pending file is signalled by a non-empty `filename`.
 * An empty `filename` means no file is waiting (use this instead of null checks).
 */

import { registerPlugin } from '@capacitor/core'

export interface PendingFileResult {
  /** Original filename as shared by the source app. Empty string = no pending file. */
  filename: string
  /** MIME type inferred from the file extension / UTI (e.g. "application/pdf"). */
  mimeType: string
  /** File contents as a base64-encoded string. Empty when filename is empty. */
  base64Data: string
  /**
   * Destination type pre-selected by the user in the Share Extension UI.
   * "event" | "task" | "handover-item" — or undefined if extension didn't set it.
   */
  destinationType?: string
}

export interface AppGroupBridgePluginInterface {
  /**
   * Reads the pending shared file from the iOS App Group container.
   * Resolves with `{ filename: "" }` when no file is waiting.
   */
  getPendingFile(): Promise<PendingFileResult>

  /**
   * Removes the staged file from the App Group container and clears the
   * UserDefaults metadata entry. Always resolves — clearing is best-effort.
   */
  clearPendingFile(): Promise<Record<string, never>>

  /**
   * Writes the JWT access token and API base URL to the App Group container
   * so the ShareExtension can authenticate API calls directly.
   * Must be called every time the token changes (login, refresh).
   */
  saveAccessToken(options: { token: string; apiBaseUrl: string }): Promise<void>

  /**
   * Removes the stored access token from the App Group container.
   * Call on logout.
   */
  clearAccessToken(): Promise<void>
}

export const AppGroupBridgePlugin = registerPlugin<AppGroupBridgePluginInterface>(
  'AppGroupBridgePlugin',
  {
    web: () =>
      import('./AppGroupBridgePluginWeb').then((m) => new m.AppGroupBridgePluginWeb()),
  },
)
